import { useQuery } from "@tanstack/react-query";
import { apiUrl } from "../lib/api";
import { useLocalization } from "../lib/i18n";

export type EngagementCategory =
  | "UPCOMING_GAME"
  | "YESTERDAY_RECAP"
  | "BREAKING_NEWS"
  | "INJURY_UPDATE"
  | "GAME_UPDATE"
  | "RESULT"
  | "UPSET"
  | "ELIMINATED"
  | "ADVANCING"
  | "SERIES_MOMENTUM"
  | "SCHEDULE"
  | "CHAMPION";

export type EngagementUrgency = "LOW" | "NORMAL" | "HIGH" | "CRITICAL";
export type EngagementTeamImpact = "POSITIVE" | "NEGATIVE" | "NEUTRAL";
export type EngagementCtaAction =
  | "NAVIGATE_MARKET"
  | "NAVIGATE_PICKS"
  | "NAVIGATE_PORTFOLIO"
  | "NAVIGATE_SETTLEMENT"
  | "PLACE_ORDER";

export interface EngagementAffectedTeam {
  team_code: string;
  impact: EngagementTeamImpact;
}

export interface EngagementFeedItem {
  id: number;
  type: EngagementCategory;
  urgency: EngagementUrgency;
  headline: string;
  body: string;
  cta_label?: string | null;
  cta_action?: EngagementCtaAction | null;
  cta_params: Record<string, unknown>;
  affected_teams: EngagementAffectedTeam[];
  published_at: string | null;
  starts_at: string;
  expires_at: string;
}

type EngagementFeedResponse = {
  generated_at: string;
  items: EngagementFeedItem[];
  next_cursor?: number | null;
};

type UseEngagementFeedArgs = {
  competitionCode: string | null;
  seasonCode: string | null;
  poolCode: string | null;
  enabled?: boolean;
};

export function useEngagementFeed({
  competitionCode,
  seasonCode,
  poolCode: _poolCode,
  enabled = true,
}: UseEngagementFeedArgs) {
  const canLoad = Boolean(enabled && competitionCode && seasonCode);
  const { locale } = useLocalization();
  const queryKey = ["engagement-feed", competitionCode ?? "none", seasonCode ?? "none", locale];

  const query = useQuery({
    queryKey,
    enabled: canLoad,
    queryFn: async () => {
      const queryParams = new URLSearchParams({
        competition_code: competitionCode!,
        season_code: seasonCode!,
        limit: "10",
      });
      queryParams.set("locale", locale);

      try {
        const response = await fetch(apiUrl(`/api/engagement/feed?${queryParams.toString()}`));
        if (!response.ok) {
          return [];
        }

        const payload: EngagementFeedResponse = await response.json();
        return payload.items ?? [];
      } catch {
        return [];
      }
    },
  });

  return {
    items: canLoad ? query.data ?? [] : [],
    isLoading: canLoad ? query.isLoading : false,
    isError: canLoad ? query.isError : false,
    reload: query.refetch,
    trackImpression: async (_itemId: number) => undefined,
    trackClick: async (_itemId: number) => undefined,
  };
}
